// Extracted from: https://github.com/pacocoursey/cmdk/blob/main/cmdk/src/command-score.ts

// The scores are arranged so that a continuous match of characters will
// result in a total score of 1.
//
// The best case, this character is a match, and either this is the start
// of the string, or the previous character was also a match.
const SCORE_CONTINUE_MATCH = 1,
	// A new match at the start of a word scores better than a new match
	// elsewhere as it's more likely that the user will type the starts
	// of fragments.
	// NOTE: We score word jumps between spaces slightly higher than slashes, brackets
	// hyphens, etc.
	SCORE_SPACE_WORD_JUMP = 0.9,
	SCORE_NON_SPACE_WORD_JUMP = 0.8,
	// Any other match isn't ideal, but we include it for completeness.
	SCORE_CHARACTER_JUMP = 0.17,
	// If the user transposed two letters, it should be significantly penalized.
	//
	// i.e. "ouch" is more likely than "curtain" when "uc" is typed.
	SCORE_TRANSPOSITION = 0.1,
	// The goodness of a match should decay slightly with each missing
	// character.
	//
	// i.e. "bad" is more likely than "bard" when "bd" is typed.
	//
	// This will not change the order of suggestions based on SCORE_* until
	// 100 characters are inserted between matches.
	PENALTY_SKIPPED = 0.999,
	// The goodness of an exact-case match should be higher than a
	// case-insensitive match by a small amount.
	//
	// i.e. "HTML" is more likely than "haml" when "HM" is typed.
	//
	// This will not change the order of suggestions based on SCORE_* until
	// 1000 characters are inserted between matches.
	PENALTY_CASE_MISMATCH = 0.9999,
	// If the word has more characters than the user typed, it should
	// be penalised slightly.
	//
	// i.e. "html" is more likely than "html5" if I type "html".
	//
	// However, it may well be the case that there's a sensible secondary
	// ordering (like alphabetical) that it makes sense to rely on when
	// there are many prefix matches, so we don't make the penalty increase
	// with the number of tokens.
	PENALTY_NOT_COMPLETE = 0.99;

const IS_GAP_REGEXP = /[\\\/_+.#"@\[\({&]/,
	COUNT_GAPS_REGEXP = /[\\\/_+.#"@\[\(\{&]/g,
	IS_SPACE_REGEXP = /[\s-]/,
	COUNT_SPACE_REGEXP = /[\s-]/g;

function commandScoreInner(
	value: string,
	search: string,
	lowerValue: string,
	lowerSearch: string,
	valueIdx: number,
	searchIdx: number,
	memoizedResults: Record<string, number>
): number {
	if (searchIdx === search.length) {
		if (valueIdx === value.length) {
			return SCORE_CONTINUE_MATCH;
		}
		return PENALTY_NOT_COMPLETE;
	}

	const memoizeKey = `${valueIdx},${searchIdx}`;
	if (memoizedResults[memoizeKey] !== undefined) {
		return memoizedResults[memoizeKey];
	}

	const abbreviationChar = lowerSearch.charAt(searchIdx);
	let index = lowerValue.indexOf(abbreviationChar, valueIdx);
	let highScore = 0;

	let score: number,
		transposedScore: number,
		wordBreaks: RegExpMatchArray | null = null,
		spaceBreaks: RegExpMatchArray | null = null;

	while (index >= 0) {
		score = commandScoreInner(
			value,
			search,
			lowerValue,
			lowerSearch,
			index + 1,
			searchIdx + 1,
			memoizedResults
		);
		if (score > highScore) {
			if (index === valueIdx) {
				score *= SCORE_CONTINUE_MATCH;
			} else if (IS_GAP_REGEXP.test(value.charAt(index - 1))) {
				score *= SCORE_NON_SPACE_WORD_JUMP;
				wordBreaks = value.slice(valueIdx, index - 1).match(COUNT_GAPS_REGEXP);
				if (wordBreaks && valueIdx > 0) {
					score *= Math.pow(PENALTY_SKIPPED, wordBreaks.length);
				}
			} else if (IS_SPACE_REGEXP.test(value.charAt(index - 1))) {
				score *= SCORE_SPACE_WORD_JUMP;
				spaceBreaks = value.slice(valueIdx, index - 1).match(COUNT_SPACE_REGEXP);
				if (spaceBreaks && valueIdx > 0) {
					score *= Math.pow(PENALTY_SKIPPED, spaceBreaks.length);
				}
			} else {
				score *= SCORE_CHARACTER_JUMP;
				if (valueIdx > 0) {
					score *= Math.pow(PENALTY_SKIPPED, index - valueIdx);
				}
			}

			if (value.charAt(index) !== search.charAt(searchIdx)) {
				score *= PENALTY_CASE_MISMATCH;
			}
		}

		if (
			(score < SCORE_TRANSPOSITION &&
				lowerValue.charAt(index - 1) === lowerSearch.charAt(searchIdx + 1)) ||
			(lowerSearch.charAt(searchIdx + 1) === lowerSearch.charAt(searchIdx) && // allow duplicate letters.
				lowerValue.charAt(index - 1) !== lowerSearch.charAt(searchIdx))
		) {
			transposedScore = commandScoreInner(
				value,
				search,
				lowerValue,
				lowerSearch,
				index + 1,
				searchIdx + 2,
				memoizedResults
			);

			if (transposedScore * SCORE_TRANSPOSITION > score) {
				score = transposedScore * SCORE_TRANSPOSITION;
			}
		}

		if (score > highScore) {
			highScore = score;
		}

		index = lowerValue.indexOf(abbreviationChar, index + 1);
	}

	memoizedResults[memoizeKey] = highScore;
	return highScore;
}

function formatInput(s: string) {
	// convert all valid space characters to space so they match each other
	return s.toLowerCase().replace(COUNT_SPACE_REGEXP, " ");
}

export function commandScore(value: string, search: string, keyword?: string[]): number {
	value = keyword && keyword.length > 0 ? `${value + " " + keyword.join(" ")}` : value;
	return commandScoreInner(value, search, formatInput(value), formatInput(search), 0, 0, {});
}
